import * as React from "react";
import { useState, useEffect } from "react";
import XpCard from "@/components/atomics/xp-card";
import { SessionRewardItemData } from "@/services/questsBackend";
import Direction from "@/components/atomics/direction";
import { BONUS_CONFIG } from "@/constants/quests";

const formatCountdown = (targetDate: string): string => {
  const now = Date.now();
  const target = new Date(targetDate).getTime();
  const diff = target - now;

  if (diff <= 0) return "00:00:00";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(
    (diff % (1000 * 60 * 60)) / (1000 * 60)
  );
  const seconds = Math.floor(
    (diff % (1000 * 60)) / 1000
  );

  return (
    `${String(hours).padStart(2, "0")}:` +
    `${String(minutes).padStart(2, "0")}:` +
    `${String(seconds).padStart(2, "0")}`
  );
};

interface QuestRewardsGridProps {
  questId: number;
  rarityLabel: string;
  rewardsCount: number;
  rewardAmount: number;
  sessions: SessionRewardItemData[];
  isAllClaimed: boolean;
  onClaimReward?: (
    cardIndex: number,
    cardType: "daily" | "bonus",
    sessionItemId: number,
    xpAmount: number
  ) => void;
  onUnlockReward?: (
    cardIndex: number,
    cardType: "daily" | "bonus",
    cardLabel: string,
    sessionId: number
  ) => void;
}

interface RewardCard {
  type: "daily" | "bonus";
  day?: number;
  label: string;
  xp: number;
  multiplier: number;
  itemId?: number;
  isClaimed: boolean;
  isMissed: boolean;
  isLocked: boolean;
  isAvailable: boolean;
  isNextLocked?: boolean;
  showCountdown?: boolean;
  sessionId?: number;
  dateOpen?: string;
  dateClose?: string;
}

function generateRewardCards(
  rarityLabel: string,
  rewardsCount: number,
  rewardAmount: number,
  sessions: SessionRewardItemData[],
  isAllClaimed: boolean
): RewardCard[] {
  const cards: RewardCard[] = [];
  const now = new Date();

  const bonusConfig = BONUS_CONFIG[rarityLabel] || {};
  const bonusThresholds = Object.keys(bonusConfig)
    .map(Number)
    .sort((a, b) => a - b);

  const dailySessions = sessions.filter(s => !s.is_bonus);
  const bonusSessions = sessions.filter(s => s.is_bonus);

  const isFirstClaim = dailySessions.length === 0 && !isAllClaimed;

  let bonusIndex = 0;
  let foundNextLocked = false;

  for (let i = 0; i < rewardsCount; i++) {
    if (i < dailySessions.length) {
      const session = dailySessions[i];

      let isAvailable = false;
      let isMissed = false;
      let isLocked = false;
      let isClaimed = false;

      if (session?.date_open && session?.date_close) {
        const dateOpen = new Date(session.date_open);
        const dateClose = new Date(session.date_close);
        isClaimed = session.rewarded;
        isAvailable = now >= dateOpen && now <= dateClose && !session.rewarded;
        isLocked = now < dateOpen && !session.rewarded;
        isMissed = now > dateClose && !session.rewarded;
      }

      const isNextLocked = isLocked && !foundNextLocked;
      if (isNextLocked) foundNextLocked = true;

      cards.push({
        type: "daily",
        day: i + 1,
        label: `day ${i + 1}`,
        xp: rewardAmount,
        multiplier: 1,
        itemId: session.id,
        isClaimed,
        isMissed,
        isLocked,
        isAvailable,
        isNextLocked,
        sessionId: session.id,
        dateOpen: session.date_open,
        dateClose: session.date_close,
      });
    } else if (i === 0 && isFirstClaim) {
      cards.push({
        type: "daily",
        day: 1,
        label: "day 1",
        xp: rewardAmount,
        multiplier: 1,
        isMissed: false,
        isClaimed: false,
        isLocked: false,
        isAvailable: true,
        sessionId: undefined,
        dateOpen: undefined,
        dateClose: undefined,
      });
    } else {
      cards.push({
        type: "daily",
        day: i + 1,
        label: `day ${i + 1}`,
        xp: rewardAmount,
        multiplier: 1,
        isMissed: false,
        isClaimed: isAllClaimed,
        isLocked: !isAllClaimed,
        isAvailable: false,
        sessionId: undefined,
        dateOpen: undefined,
        dateClose: undefined,
      });
    }

    const dayNumber = i + 1;
    const threshold = bonusThresholds[bonusIndex];

    if (threshold && dayNumber === threshold) {
      const bonusMultiplier = bonusConfig[threshold.toString()];
      const bonusSession = bonusSessions[bonusIndex];

      if (bonusSession) {
        const isClaimed = bonusSession.rewarded;
        const isAvailable = !bonusSession.rewarded;

        cards.push({
          type: "bonus",
          label: "bonus",
          xp: rewardAmount * bonusMultiplier,
          multiplier: bonusMultiplier,
          itemId: bonusSession.id,
          isClaimed,
          isMissed: false,
          isLocked: false,
          isAvailable,
          sessionId: bonusSession.id,
          dateOpen: bonusSession.date_open,
          dateClose: bonusSession.date_close,
        });
      } else {
        cards.push({
          type: "bonus",
          label: "bonus",
          xp: rewardAmount * bonusMultiplier,
          multiplier: bonusMultiplier,
          isClaimed: false,
          isMissed: false,
          isLocked: true,
          isAvailable: false,
          sessionId: undefined,
          dateOpen: undefined,
          dateClose: undefined,
        });
      }

      bonusIndex++;
    }
  }

  // Set showCountdown on the first locked card after a claimed card
  for (let i = 1; i < cards.length; i++) {
    const prevCard = cards[i - 1];
    const currentCard = cards[i];
    if (prevCard.isClaimed && currentCard.isLocked && currentCard.dateOpen) {
      currentCard.showCountdown = true;
      break;
    }
  }

  return cards;
}

export default function QuestRewardsGrid({
  questId,
  rarityLabel,
  rewardsCount,
  rewardAmount,
  sessions,
  isAllClaimed,
  onClaimReward,
  onUnlockReward,
}: QuestRewardsGridProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [countdown, setCountdown] = useState<string>("");

  const rewardCards = generateRewardCards(
    rarityLabel,
    rewardsCount,
    rewardAmount,
    sessions,
    isAllClaimed
  );

  const countdownCard = rewardCards.find(card => card.showCountdown);

  // Auto-scroll to the first unclaimed card on mount
  useEffect(() => {
    const CARDS_PER_PAGE = 4;
    // Find the first unclaimed card index
    const firstUnclaimedIndex = rewardCards.findIndex(card => !card.isClaimed);
    
    if (firstUnclaimedIndex > 0) {
      // Calculate the scroll position to show the unclaimed card
      // We want the unclaimed card to be visible, preferably at the start
      const maxIndex = Math.max(0, rewardCards.length - CARDS_PER_PAGE);
      const targetIndex = Math.min(firstUnclaimedIndex, maxIndex);
      setCurrentIndex(targetIndex);
    }
  }, []);

  useEffect(() => {
    if (countdownCard?.dateOpen) {
      setCountdown(formatCountdown(countdownCard.dateOpen));

      const interval = setInterval(() => {
        const newCountdown = formatCountdown(countdownCard.dateOpen!);
        setCountdown(newCountdown);

        if (newCountdown === "00:00:00") {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [countdownCard?.dateOpen]);

  const CARDS_PER_PAGE = 4;
  const totalCards = rewardCards.length;
  const showNavigation = totalCards > CARDS_PER_PAGE;

  const handleNext = () => {
    if (currentIndex + CARDS_PER_PAGE < totalCards) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex + CARDS_PER_PAGE < totalCards;

  const translateX = -(currentIndex * 27);

  const handleCardClick = (card: RewardCard) => {
    if (card.isAvailable) {
      // For first claim (no session yet), itemId is undefined - pass 0
      onClaimReward?.(questId, card.type, card.itemId || 0, card.xp);
    }
    if (card.isMissed && card.itemId) {
      onUnlockReward?.(questId, card.type, card.label, card.itemId);
    }
  };

  return (
    <div className="w-full bg-dark-navy pt-[13px] pb-[4px]">
      <div className="relative pl-[24px] pr-[14px] flex items-center">
        {showNavigation && (
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className={`absolute left-[-2px] top-[40%] -translate-y-1/2 z-10 w-[24px] h-[24px] flex items-center justify-center bg-transparent transition-opacity ${
              canGoPrevious
                ? "opacity-100 cursor-pointer"
                : "opacity-30 cursor-not-allowed"
            }`}
          >
            <Direction
              className="w-[7px] h-[12px] rotate-180"
              color="#00ffff"
            />
          </button>
        )}

        <div className="flex-1 overflow-hidden pt-[10px] pr-[20px]">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(${translateX}%)` }}
          >
            {rewardCards.map((card, index) => {
              const prevCard = index > 0 ? rewardCards[index - 1] : null;
              const showConnectionLine =
                prevCard && prevCard.isClaimed && card.isClaimed;

              return (
                <React.Fragment key={`${card.type}-${index}`}>
                  {showConnectionLine && index > 0 && (
                    <div className="flex-shrink-0 relative w-[7%]">
                      <div className="absolute top-[40%] left-0 right-0 h-[3px] bg-quest-green" />
                    </div>
                  )}
                  {!showConnectionLine && index > 0 && (
                    <div className="flex-shrink-0 w-[7%]" />
                  )}

                  <div className="w-[20%] flex-shrink-0 flex flex-col items-center gap-1">
                    <div
                      className={`w-full aspect-square relative ${
                        card.isLocked || card.isClaimed
                          ? "cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                      onClick={() => handleCardClick(card)}
                    >
                      <XpCard
                        className="w-full h-full"
                        multiplier={card.multiplier}
                        is_active={card.isAvailable}
                        claimed={card.isClaimed}
                        isMissed={card.isMissed}
                        isLocked={card.isLocked}
                      />
                      {card.showCountdown && countdown && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          <span className="text-[14px] text-white whitespace-nowrap">
                            {countdown}
                          </span>
                        </div>
                      )}
                    </div>
                    <span
                      className={`text-[12px] uppercase text-center ${
                        card.isClaimed
                          ? "text-green-500"
                          : card.isMissed
                            ? "text-primary-red"
                            : card.isLocked
                              ? "text-primary-gray"
                              : "text-primary-cyan"
                      }`}
                    >
                      {card.label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {showNavigation && (
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className={`absolute right-[3px] top-[40%] -translate-y-1/2 z-10 w-[24px] h-[24px] flex items-center justify-center bg-transparent transition-opacity ${
              canGoNext
                ? "opacity-100 cursor-pointer"
                : "opacity-30 cursor-not-allowed"
            }`}
          >
            <Direction className="w-[7px] h-[12px]" color="#00ffff" />
          </button>
        )}
      </div>
    </div>
  );
}
